import { Phone, Trash2, User } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

import { relativeTime } from '@/lib/format'

import { useActiveAllocations, useRevokeAllocation } from './api'

/**
 * The right-rail card on a case: which agents are assigned, on which slot,
 * since when. Revoke is a one-click action (manager permission gated server-side).
 */
export function CaseAllocationsCard({ caseId }: { caseId: string }) {
  const { data, isPending } = useActiveAllocations(caseId)
  const revoke = useRevokeAllocation(caseId)

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Allocations</CardTitle>
            <CardDescription>Active assignments on this case.</CardDescription>
          </div>
          {data && <Badge tone="navy">{data.length}</Badge>}
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {isPending && (
          <div className="space-y-2 px-5 pb-4">
            {[0, 1].map((i) => <Skeleton key={i} className="h-9 w-full" />)}
          </div>
        )}
        {!isPending && (data?.length ?? 0) === 0 && (
          <div className="px-5 pb-4 text-center">
            <p className="text-sm font-medium text-navy-900">No active allocations</p>
            <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
              A manager can allocate this case from the Allocations page.
            </p>
          </div>
        )}
        {data && data.length > 0 && (
          <ul className="divide-y divide-slate-200">
            {data.map((a) => (
              <li key={a.allocationId} className="flex items-center gap-3 px-5 py-3">
                <span
                  className={
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full ' +
                    (a.slot === 'TELE' ? 'bg-info-soft text-info' : 'bg-positive-soft text-positive')
                  }
                >
                  {a.slot === 'TELE' ? <Phone className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium text-navy-900">{a.agentEmpId}</span>
                    <Badge tone={a.slot === 'TELE' ? 'info' : 'positive'}>{a.slot}</Badge>
                  </div>
                  <p className="text-xs text-navy-800/90">{a.agentFullName}</p>
                  <p className="text-2xs uppercase tracking-wider text-muted-foreground">
                    Since {relativeTime(a.allocatedAt)}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Server-side allocation is invariant-checked on the
                    // OPEN case, so revoking by accident is recoverable
                    // (manager can re-allocate) — but it still stamps an
                    // audit event and notifies the affected agent. A short
                    // confirm prevents fat-fingers.
                    const ok = window.confirm(
                      `Revoke ${a.agentEmpId}'s ${a.slot} allocation on this case?\n\n`
                      + 'The agent will lose this case from their queue and an'
                      + ' ALLOCATION.REVOKED event is logged with reason MANAGER_REVOKE.',
                    )
                    if (ok) revoke.mutate({ allocationId: a.allocationId, reason: 'MANAGER_REVOKE' })
                  }}
                  disabled={revoke.isPending}
                  title="Revoke allocation"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
